
// src/store/slices/canteenSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import canteenApi from '../../api/canteenApi';

const initialState = {
  menus: [],
  isLoading: false,
  error: null,
  searchTerm: '', // ← Added
};

export const fetchMenus = createAsyncThunk(
  'canteen/fetchMenus',
  async (search = '', { getState }) => {
    const currentSearch = search || getState().canteen.searchTerm;
    return await canteenApi.getMenus(currentSearch);
  }
);

// export const bulkCreateMenus = createAsyncThunk('canteen/bulkCreateMenus', async (menus) => {
//   return await canteenApi.bulkCreateMenus(menus);
// });

export const bulkCreateMenus = createAsyncThunk(
  'canteen/bulkCreateMenus',
  async (menus, { rejectWithValue }) => {
    try {
      const res = await canteenApi.bulkCreateMenus(menus);
      return res.data;
    } catch (err) {
      console.log("ERROR:", err.response.data.name[0]);
      // This is critical: return the full response data
      return rejectWithValue(err.response.data.name[0] || 'Failed to create menus');
    }
  }
);

export const updateMenu = createAsyncThunk('canteen/updateMenu', async ({ id, data }) => {
  return await canteenApi.updateMenu(id, data);
});

export const deleteMenu = createAsyncThunk('canteen/deleteMenu', async (id) => {
  await canteenApi.deleteMenu(id);
  return id;
});

export const fetchIngredients = createAsyncThunk('canteen/fetchIngredients', async (menuId) => {
  const ingredients = await canteenApi.getIngredients(menuId);
  return { menuId, ingredients };
});

 export const bulkCreateIngredients = createAsyncThunk('canteen/bulkCreateIngredients', async (ingredients) => {
  return await canteenApi.bulkCreateIngredients(ingredients);
});

export const updateIngredient = createAsyncThunk('canteen/updateIngredient', async ({ id, data }) => {
  return await canteenApi.updateIngredient(id, data);
});

export const deleteIngredient = createAsyncThunk('canteen/deleteIngredient', async (id) => {
  await canteenApi.deleteIngredient(id);
  return id;
});

export const toggleMenuStock = createAsyncThunk(
  'canteen/toggleMenuStock',
  async (menuId, { rejectWithValue }) => {
    try {
      const updatedMenu = await canteenApi.toggleStock(menuId);
      return updatedMenu; // { id, name, in_stock, ... }
    } catch (err) {
      return rejectWithValue(err.response?.data || 'Failed to toggle stock');
    }
  }
);

const canteenSlice = createSlice({
  name: 'canteen',
  initialState,
  reducers: {
    clearError: (s) => { s.error = null; },
    setSearchTerm: (state, action) => {
      state.searchTerm = action.payload;
    },
  },
  extraReducers: (b) => {
    b
      .addCase(fetchMenus.pending, (s) => { s.isLoading = true; })
      .addCase(fetchMenus.fulfilled, (s, a) => {
        s.isLoading = false;
        s.menus = (Array.isArray(a.payload) ? a.payload : []).map(menu => ({
          ...menu,
          ingredients: menu.ingredients || null
        }));
      })
      .addCase(fetchMenus.rejected, (s, a) => {
        s.isLoading = false;
        s.error = a.error.message;
      })
      .addCase(bulkCreateMenus.fulfilled, (s, a) => { s.menus.push(...a.payload); })
      .addCase(updateMenu.fulfilled, (s, a) => {
        const idx = s.menus.findIndex(m => m.id === a.payload.id);
        if (idx > -1) s.menus[idx] = a.payload;
      })
      .addCase(deleteMenu.fulfilled, (s, a) => {
        s.menus = s.menus.filter(m => m.id !== a.payload);
      })
      .addCase(fetchIngredients.fulfilled, (s, a) => {
        const menu = s.menus.find(m => m.id === a.payload.menuId);
        if (menu) menu.ingredients = a.payload.ingredients;
      })
      .addCase(bulkCreateIngredients.fulfilled, (s, a) => {
        const menu = s.menus.find(m => a.payload[0]?.menu === m.id);
        if (menu) menu.ingredients = [...(menu.ingredients || []), ...a.payload];
      })
      .addCase(bulkCreateMenus.rejected, (state, action) => {
  // Preserve the full error payload for component to use
  state.error = action.payload || action.error.message;
})
      .addCase(updateIngredient.fulfilled, (s, a) => {
        s.menus.forEach(m => {
          if (m.ingredients) {
            const idx = m.ingredients.findIndex(i => i.id === a.payload.id);
            if (idx > -1) m.ingredients[idx] = a.payload;
          }
        });
      })
      .addCase(deleteIngredient.fulfilled, (s, a) => {
        s.menus.forEach(m => {
          if (m.ingredients) m.ingredients = m.ingredients.filter(i => i.id !== a.payload);
        });
      })
      // Inside extraReducers
// .addCase(toggleMenuStock.fulfilled, (state, action) => {
//   const menu = action.payload;
//   const idx = state.menus.findIndex(m => m.id === menu.id);
//   if (idx > -1) state.menus[idx] = menu;
// })
// Inside extraReducers
.addCase(toggleMenuStock.fulfilled, (state, action) => {
  const { id, in_stock, name } = action.payload;

  const idx = state.menus.findIndex(m => m.id === id);
  if (idx !== -1) {
    state.menus[idx].in_stock = in_stock;
    if (name) state.menus[idx].name = name; // optional, only if you want fresh name
  }
})
.addCase(toggleMenuStock.rejected, (state, action) => {
  toast.error(action.payload || 'Failed to update stock');
});
  },
});

export const { clearError, setSearchTerm } = canteenSlice.actions;
export default canteenSlice.reducer;